import { Suspense } from "react";
import { lazyWithRetry as lazy } from "@/lib/lazyWithRetry";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes, Navigate } from "react-router-dom";
import * as Sentry from "@sentry/react";
import { Analytics } from "@vercel/analytics/react";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider } from "@/hooks/useAuth";
import { OnboardingProvider } from "@/components/onboarding/OnboardingProvider";
import RouteErrorBoundary from "@/components/RouteErrorBoundary";
import TopProgressBar from "@/components/TopProgressBar";
import CookieBanner from "@/components/CookieBanner";
import Login from "@/pages/Login";
import { posRoutes } from "@/routes/posRoutes";
import { stockRoutes } from "@/routes/stockRoutes";
import { achatsMetierRoutes } from "@/routes/achatsMetierRoutes";
import { rapportsRoutes } from "@/routes/rapportsRoutes";
import { configRoutes } from "@/routes/configRoutes";
import { membresPromotionsRoutes } from "@/routes/membresPromotionsRoutes";
import { regieEvenementsRoutes } from "@/routes/regieEvenementsRoutes";

const AppLayout = lazy(() => import("@/components/layout/AppLayout"));
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const MonComptePage = lazy(() => import("@/pages/account/MonComptePage"));
const AidePage = lazy(() => import("@/pages/aide/AidePage"));
const NotFound = lazy(() => import("@/pages/NotFound"));
const ForgotPasswordPage = lazy(() => import("@/pages/auth/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("@/pages/auth/ResetPasswordPage"));
const ConfidentialitePage = lazy(() => import("@/pages/legal/ConfidentialitePage"));
const CGUPage = lazy(() => import("@/pages/legal/CGUPage"));

const queryClient = new QueryClient();

const ErrorFallback = () => (
  <div
    style={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      height: "100vh",
      fontFamily: "sans-serif",
    }}
  >
    <h2>Une erreur est survenue</h2>
    <p>L'équipe technique a été notifiée.</p>
    <button onClick={() => (window.location.href = "/dashboard")}>
      Retour au dashboard
    </button>
  </div>
);

const RouteFallback = () => <TopProgressBar />;

const App = () => (
  <Sentry.ErrorBoundary fallback={<ErrorFallback />}>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <AuthProvider>
            <OnboardingProvider>
              <RouteErrorBoundary routeName="root">
                <Suspense fallback={<RouteFallback />}>
                  <Routes>
                    {/* Routes publiques */}
                    <Route path="/login" element={<Login />} />
                    <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                    <Route path="/reset-password" element={<ResetPasswordPage />} />
                    <Route path="/confidentialite" element={<ConfidentialitePage />} />
                    <Route path="/cgu" element={<CGUPage />} />
                    <Route path="/" element={<Navigate to="/dashboard" replace />} />

                    {/* Routes POS plein écran (serveur, KDS, caisse, grille tampon) */}
                    {posRoutes()}

                    {/* Routes avec sidebar (AppLayout) */}
                    <Route element={<AppLayout />}>
                      <Route path="/dashboard" element={<Dashboard />} />
                      <Route path="/mon-compte" element={<MonComptePage />} />
                      <Route path="/aide" element={<AidePage />} />
                      {stockRoutes()}
                      {achatsMetierRoutes()}
                      {rapportsRoutes()}
                      {configRoutes()}
                      {membresPromotionsRoutes()}
                      {regieEvenementsRoutes()}
                      <Route path="*" element={<NotFound />} />
                    </Route>
                  </Routes>
                </Suspense>
              </RouteErrorBoundary>
            </OnboardingProvider>
          </AuthProvider>
          <CookieBanner />
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
    {/* Vercel Analytics : volontairement hors du tree React applicatif
        pour éviter un re-mount du AuthProvider (lock GoTrue orphelin). */}
    <Analytics />
  </Sentry.ErrorBoundary>
);

export default App;
